import React, { useEffect, useMemo, useState } from 'react';
import { Button, Form, Input, Table, message } from 'antd';
import {
  LogoutOutlined,
  MailOutlined,
  MessageOutlined,
  PhoneOutlined,
  SendOutlined,
  ShopOutlined
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useNavigate } from 'react-router-dom';
import { useStore } from '../../../store/useStore';
import { useTranslation } from '../../../i18n';
import { PlanMemberMark } from '../../../components/PlanMemberMark';
import { planMemberTier } from '../pricing/pricingUtils';
import { useCurrentAccount, useRefreshCurrentAccount } from '../layout/currentAccountContext';
import { changePassword, getAccountAddonQuota, getPurchasedOrders } from '../../../store/request';
import type { AccountIdentityType, AddonQuotaBalance } from '../../../types/account';
import type { PurchasedOrder } from '../../../types/order';
import { formatFullTime } from '../../../utils/timeFormat';
import { userInitial } from '../../../utils/userInitial';
import { BindIdentityModal } from './BindIdentityModal';
import { ChangeIdentityModal } from './ChangeIdentityModal';
import { UnbindIdentityModal } from './UnbindIdentityModal';
import {
  containsPasswordWhitespace,
  emptyUserCenterProfile,
  getPasswordStrength,
  isBasicMembership,
  mapAccountToProfile,
  maskPhone,
  utf8ByteLength
} from './userCenterData';

type ProfileTab = 'profile' | 'security' | 'orders';
type OrderSubTab = 'package' | 'benefit';

function formatPaidAt(value?: string) {
  const text = formatFullTime(value);
  return text || '—';
}

function formatOrderAmount(order: PurchasedOrder) {
  if (typeof order.amountMinor !== 'number' || !Number.isFinite(order.amountMinor)) return '—';
  const digits =
    typeof order.currencyFractionDigits === 'number' && order.currencyFractionDigits >= 0
      ? order.currencyFractionDigits
      : 0;
  const major = order.amountMinor / 10 ** digits;
  const text = major.toLocaleString(undefined, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits
  });
  return order.currency?.trim() ? `${order.currency.trim()} ${text}` : text;
}

function featureSummary(order: PurchasedOrder) {
  return (order.featureDescriptions ?? [])
    .map((item) => item.title?.trim())
    .filter((title): title is string => Boolean(title))
    .join('、');
}

function fulfillmentClass(status?: string) {
  if (status === 'FULFILLED') return 'completed';
  if (status === 'MANUAL_REVIEW') return 'review';
  if (status === 'PENDING') return 'pending';
  return '';
}

function addonCount(value?: number) {
  if (typeof value !== 'number' || !Number.isFinite(value)) return null;
  return Math.trunc(value).toLocaleString('en-US');
}

function usagePercent(used: number, limit: number) {
  if (!(limit > 0)) return 0;
  return Math.min(100, Math.max(0, Math.round((used / limit) * 100)));
}

function UsageBar({ percent }: { percent: number }) {
  const value = Math.min(100, Math.max(0, percent));
  return (
    <div className='uc-usage-bar' role='progressbar' aria-valuenow={value} aria-valuemin={0} aria-valuemax={100}>
      <div className='uc-usage-bar-fill' style={{ width: `${value}%` }} />
    </div>
  );
}

type SecurityFormValues = {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
};

function AddonMetric({
  tone,
  icon,
  label,
  value,
  before,
  after
}: {
  tone: 'shop' | 'dm' | 'invite' | 'mail';
  icon: React.ReactNode;
  label: string;
  value: string | null;
  before?: string;
  after?: string;
}) {
  return (
    <div className='uc-addon-item'>
      <span className={`uc-addon-icon uc-addon-icon--${tone}`}>{icon}</span>
      <div className='uc-addon-copy'>
        <div className='uc-addon-label'>{label}</div>
        <div className='uc-addon-figure'>
          {value == null ? (
            <span className='uc-addon-num is-empty'>—</span>
          ) : (
            <>
              {before ? <span className='uc-addon-unit'>{before}</span> : null}
              <span className='uc-addon-num'>{value}</span>
              {after ? <span className='uc-addon-unit'>{after}</span> : null}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function ProfileInfoItem({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="uc-info-row">
      <div className="uc-info-label">{label}</div>
      <div className="uc-info-value">{children}</div>
    </div>
  );
}

export function UserCenter() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { loginStore } = useStore();
  const [tab, setTab] = useState<ProfileTab>('profile');
  const [orderSubTab, setOrderSubTab] = useState<OrderSubTab>('package');
  const [profile, setProfile] = useState(emptyUserCenterProfile);
  const [orders, setOrders] = useState<PurchasedOrder[]>([]);
  const [ordersTotal, setOrdersTotal] = useState(0);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [orderPageNum, setOrderPageNum] = useState(1);
  const [orderPageSize, setOrderPageSize] = useState(10);
  const [securityForm] = Form.useForm<SecurityFormValues>();
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [unbindTarget, setUnbindTarget] = useState<{
    identityType: AccountIdentityType;
    identity: string;
  } | null>(null);
  const [changeTarget, setChangeTarget] = useState<{
    identityType: AccountIdentityType;
    identity: string;
  } | null>(null);
  const [bindTarget, setBindTarget] = useState<AccountIdentityType | null>(null);
  const [addonQuota, setAddonQuota] = useState<AddonQuotaBalance | null>(null);
  const account = useCurrentAccount();
  const refreshAccount = useRefreshCurrentAccount();

  useEffect(() => {
    setProfile(mapAccountToProfile(account));
  }, [account]);

  useEffect(() => {
    if (tab !== 'orders') return;
    let cancelled = false;
    setOrdersLoading(true);
    getPurchasedOrders({ pageNum: orderPageNum, pageSize: orderPageSize })
      .then((res) => {
        if (cancelled) return;
        if (res.code !== 200 || !res.content) {
          if (res.code !== -1) message.error(res.message || t('userCenter.orders_load_failed'));
          setOrders([]);
          setOrdersTotal(0);
          return;
        }
        setOrders(Array.isArray(res.content.records) ? res.content.records : []);
        setOrdersTotal(res.content.total ?? 0);
      })
      .catch(() => {
        if (!cancelled) message.error(t('userCenter.orders_load_failed'));
      })
      .finally(() => {
        if (!cancelled) setOrdersLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [tab, orderPageNum, orderPageSize, t]);

  useEffect(() => {
    let cancelled = false;
    getAccountAddonQuota()
      .then((res) => {
        if (cancelled) return;
        if (res.code !== 200 || !res.content) {
          if (res.code !== -1) message.error(res.message || t('userCenter.addon_load_failed'));
          setAddonQuota(null);
          return;
        }
        setAddonQuota(res.content);
      })
      .catch(() => {
        if (!cancelled) message.error(t('userCenter.addon_load_failed'));
      });
    return () => {
      cancelled = true;
    };
  }, [t]);

  const addonShopCount = addonCount(addonQuota?.shop?.total);
  const addonDmCount = addonCount(addonQuota?.dm?.remaining);
  const addonInviteCount = addonCount(addonQuota?.invite?.remaining);
  const addonMailCount = addonCount(addonQuota?.mail?.remaining);
  const newPassword = Form.useWatch('newPassword', securityForm) ?? '';
  const strengthLevel = getPasswordStrength(newPassword);
  const strengthLabel =
    strengthLevel === 1
      ? t('userCenter.strength_weak')
      : strengthLevel === 2
        ? t('userCenter.strength_mid')
        : strengthLevel === 3
          ? t('userCenter.strength_strong')
          : '';

  const tabs = useMemo(
    () => [
      { key: 'profile' as const, label: t('userCenter.tab_profile') },
      { key: 'security' as const, label: t('userCenter.tab_security') },
      { key: 'orders' as const, label: t('userCenter.tab_orders') }
    ],
    [t]
  );

  const avatarLetter = userInitial(profile.userName);
  const planTier = planMemberTier({
    displayName: profile.planName,
    grade: profile.planGrade ?? undefined
  });
  const planBadgeClass = `uc-plan-badge${planTier ? ` uc-plan-badge--${planTier}` : ''}`;

  const visibleOrders = useMemo(() => {
    const orderType = orderSubTab === 'package' ? 'PLAN' : 'ENTITLEMENT';
    return orders.filter((order) => order.orderType === orderType);
  }, [orders, orderSubTab]);

  const logout = () => {
    loginStore.logout();
    navigate('/creator/login');
  };

  const savePassword = async (values: SecurityFormValues) => {
    if (passwordSaving) return;
    const oldPassword = values.currentPassword ?? '';
    const newPassword = values.newPassword ?? '';
    if (newPassword !== values.confirmPassword) {
      message.error(t('userCenter.password_mismatch'));
      return;
    }
    setPasswordSaving(true);
    const res = await changePassword({ oldPassword, newPassword });
    setPasswordSaving(false);
    if (res.code !== 200) {
      if (res.code !== -1) message.error(res.message || t('userCenter.password_change_failed'));
      return;
    }
    message.success(res.message || t('userCenter.password_changed_relogin'));
    securityForm.resetFields();
    loginStore.logout();
    navigate('/creator/login');
  };

  const orderColumns: ColumnsType<PurchasedOrder> = useMemo(() => {
    const statusLabel: Record<string, string> = {
      PENDING: t('userCenter.fulfillment_PENDING'),
      FULFILLED: t('userCenter.fulfillment_FULFILLED'),
      MANUAL_REVIEW: t('userCenter.fulfillment_MANUAL_REVIEW')
    };
    return [
      {
        title: t('userCenter.col_order_no'),
        dataIndex: 'orderNo',
        width: 200,
        ellipsis: true,
        render: (value?: string) => value?.trim() || '—'
      },
      {
        title: t('userCenter.col_package'),
        dataIndex: 'name',
        width: 220,
        render: (_value: string | undefined, order) => {
          const days = order.orderType === 'ENTITLEMENT' ? order.validityDays : order.durationDays;
          const summary = featureSummary(order);
          return (
            <div className='min-w-0'>
              <div className='truncate'>
                {order.name?.trim() || '—'}
                {typeof days === 'number' ? ` · ${t('userCenter.order_days', { days })}` : ''}
              </div>
              {summary ? <div className='truncate text-xs text-slate-400'>{summary}</div> : null}
            </div>
          );
        }
      },
      {
        title: t('userCenter.col_amount'),
        key: 'amount',
        width: 140,
        render: (_value, order) => <span className='uc-money'>{formatOrderAmount(order)}</span>
      },
      {
        title: t('userCenter.col_status'),
        dataIndex: 'fulfillmentStatus',
        width: 120,
        render: (status?: string) => {
          const key = status?.trim() ?? '';
          const tone = fulfillmentClass(key);
          return (
            <span className={`uc-status-pill${tone ? ` uc-status-pill--${tone}` : ''}`}>
              <i className='uc-status-dot' />
              {statusLabel[key] || key || '—'}
            </span>
          );
        }
      },
      {
        title: t('userCenter.col_purchased_at'),
        dataIndex: 'paidAt',
        width: 180,
        render: (value?: string) => formatPaidAt(value)
      }
    ];
  }, [t]);

  return (
    <div className='container flex flex-col !overflow-hidden'>
      <header className="mb-5 flex items-center justify-between gap-4">
        <div className="min-w-0">
          <h1 className="mb-2 mt-0 text-[22px] font-semibold leading-snug tracking-tight text-slate-800">
            {t('layout.profile')}
          </h1>
        </div>
      </header>
      <div className='uc-page min-h-0 flex-1'>
        <aside className='uc-sidebar'>
          <div className='uc-sidebar-profile'>
            <div className='uc-avatar' aria-hidden>
              {avatarLetter}
            </div>
            <div className='uc-sidebar-name'>{profile.userName || '—'}</div>
            {profile.planName ? (
              <span className={planBadgeClass}>
                <PlanMemberMark
                  name={profile.planName}
                  grade={profile.planGrade}
                  className='plan-member-mark plan-member-mark--badge'
                />
                {profile.planName}
              </span>
            ) : null}
          </div>
          <div className='uc-sidebar-meta'>
            <div className='uc-sidebar-meta-item'>
              <MailOutlined />
              <span>{profile.email || profile.platformEmail || '—'}</span>
            </div>
            <div className='uc-sidebar-meta-item'>
              <PhoneOutlined />
              <span>{maskPhone(profile.phone)}</span>
            </div>
          </div>
          <Button
            block
            className='uc-logout-btn'
            icon={<LogoutOutlined />}
            onClick={logout}
          >
            {t('common.logout')}
          </Button>
        </aside>

        <section className='uc-main'>
          <div className='uc-tabs' role='tablist'>
            {tabs.map((item) => (
              <button
                key={item.key}
                type='button'
                role='tab'
                aria-selected={tab === item.key}
                className={`uc-tab${tab === item.key ? ' is-active' : ''}`}
                onClick={() => setTab(item.key)}
              >
                {item.label}
              </button>
            ))}
          </div>

          <div className='uc-main-body'>
            <div className={tab === 'profile' ? 'uc-tab-panel' : 'uc-tab-panel hidden'}>
              <div className='uc-section'>
                <h3 className='uc-section-title'>{t('userCenter.basic_info')}</h3>
                <div className='uc-info-grid'>
                  <ProfileInfoItem label={t('login.username_label')}>
                    {profile.userName?.trim() || '—'}
                  </ProfileInfoItem>
                  <ProfileInfoItem label={t('userCenter.phone')}>
                    {profile.phone ? maskPhone(profile.phone) : '—'}
                  </ProfileInfoItem>
                  <ProfileInfoItem label={t('layout.menu_email')}>
                    {profile.email?.trim() || '—'}
                  </ProfileInfoItem>
                  <ProfileInfoItem label={t('userCenter.platform_email')}>
                    <span className='inline-flex min-w-0 flex-wrap items-center gap-2'>
                      <span className='min-w-0 break-all'>{profile.platformEmail?.trim() || '—'}</span>
                      {profile.platformEmail?.trim() ? (
                        <span className='uc-input-tag'>{t('userCenter.wowbit_assigned')}</span>
                      ) : null}
                    </span>
                  </ProfileInfoItem>
                </div>
              </div>

              <div className='uc-section'>
                <h3 className='uc-section-title'>{t('userCenter.plan_section')}</h3>
                <div className='uc-plan-banner'>
                  <PlanMemberMark
                    name={profile.planName}
                    grade={profile.planGrade}
                    className='uc-plan-mark'
                  />
                  <div className='uc-plan-info'>
                    <div className='uc-plan-info-row'>
                      <span className='uc-plan-label'>{t('userCenter.current_plan')}</span>
                      {profile.planName ? (
                        <span className={planBadgeClass}>{profile.planName}</span>
                      ) : (
                        <span className='text-slate-400'>—</span>
                      )}
                    </div>
                    {profile.expireDate ? (
                      <p className='uc-plan-expire'>
                        {t('userCenter.expire_remain', {
                          date: profile.expireDate,
                          days: profile.remainDays
                        })}
                      </p>
                    ) : null}
                  </div>
                  <div className='uc-plan-action'>
                    <Button
                      className='uc-outline-btn'
                      onClick={() => navigate('/creator/upgrade')}
                    >
                      {t(isBasicMembership(profile) ? 'userCenter.go_upgrade' : 'userCenter.go_renew')}
                    </Button>
                  </div>
                </div>
              </div>

              <div className='uc-section'>
                <h3 className='uc-section-title'>{t('userCenter.addon_section')}</h3>
                <div className='uc-addon-board'>
                  <AddonMetric
                    tone='shop'
                    icon={<ShopOutlined />}
                    label={t('userCenter.addon_shop')}
                    value={addonShopCount}
                    after={t('userCenter.addon_shop_unit')}
                  />
                  <AddonMetric
                    tone='dm'
                    icon={<MessageOutlined />}
                    label={t('userCenter.addon_dm')}
                    value={addonDmCount}
                    before={t('userCenter.addon_remain_before')}
                    after={t('userCenter.addon_remain_after')}
                  />
                  <AddonMetric
                    tone='invite'
                    icon={<SendOutlined />}
                    label={t('userCenter.addon_invite')}
                    value={addonInviteCount}
                    before={t('userCenter.addon_remain_before')}
                    after={t('userCenter.addon_remain_after')}
                  />
                  <AddonMetric
                    tone='mail'
                    icon={<MailOutlined />}
                    label={t('userCenter.addon_mail')}
                    value={addonMailCount}
                    before={t('userCenter.addon_remain_before')}
                    after={t('userCenter.addon_remain_after')}
                  />
                </div>
              </div>
            </div>

            <div className={tab === 'security' ? 'uc-tab-panel' : 'uc-tab-panel hidden'}>
              <div className='uc-section'>
                <h3 className='uc-section-title'>{t('userCenter.login_methods')}</h3>
                <div className='space-y-3'>
                  <div className='flex flex-wrap items-center justify-between gap-3 rounded-lg border border-[#eef0f4] px-4 py-3'>
                    <div className='min-w-0'>
                      <div className='text-sm font-medium text-slate-700'>{t('userCenter.phone')}</div>
                      <div className='mt-0.5 text-sm text-slate-500'>
                        {profile.phone ? maskPhone(profile.phone) : '—'}
                      </div>
                    </div>
                    <div className='flex shrink-0 flex-wrap gap-2'>
                      {profile.phone.trim() ? (
                        <>
                          <Button
                            autoInsertSpace={false}
                            className='uc-identity-btn'
                            onClick={() =>
                              setChangeTarget({ identityType: 'PHONE', identity: profile.phone.trim() })
                            }
                          >
                            {t('userCenter.change')}
                          </Button>
                          <Button
                            danger
                            autoInsertSpace={false}
                            className='uc-identity-btn'
                            onClick={() =>
                              setUnbindTarget({ identityType: 'PHONE', identity: profile.phone.trim() })
                            }
                          >
                            {t('userCenter.unbind')}
                          </Button>
                        </>
                      ) : (
                        <Button
                          type='primary'
                          autoInsertSpace={false}
                          className='uc-identity-btn'
                          onClick={() => setBindTarget('PHONE')}
                        >
                          {t('userCenter.bind')}
                        </Button>
                      )}
                    </div>
                  </div>
                  <div className='flex flex-wrap items-center justify-between gap-3 rounded-lg border border-[#eef0f4] px-4 py-3'>
                    <div className='min-w-0'>
                      <div className='text-sm font-medium text-slate-700'>{t('layout.menu_email')}</div>
                      <div className='mt-0.5 truncate text-sm text-slate-500'>
                        {profile.email?.trim() || '—'}
                      </div>
                    </div>
                    <div className='flex shrink-0 flex-wrap gap-2'>
                      {profile.email.trim() ? (
                        <>
                          <Button
                            autoInsertSpace={false}
                            className='uc-identity-btn'
                            onClick={() =>
                              setChangeTarget({
                                identityType: 'EMAIL',
                                identity: profile.email.trim()
                              })
                            }
                          >
                            {t('userCenter.change')}
                          </Button>
                          <Button
                            danger
                            autoInsertSpace={false}
                            className='uc-identity-btn'
                            onClick={() =>
                              setUnbindTarget({
                                identityType: 'EMAIL',
                                identity: profile.email.trim()
                              })
                            }
                          >
                            {t('userCenter.unbind')}
                          </Button>
                        </>
                      ) : (
                        <Button
                          type='primary'
                          autoInsertSpace={false}
                          className='uc-identity-btn'
                          onClick={() => setBindTarget('EMAIL')}
                        >
                          {t('userCenter.bind')}
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className='uc-section'>
                <h3 className='uc-section-title'>{t('userCenter.change_password')}</h3>
                <Form
                  form={securityForm}
                  layout='vertical'
                  requiredMark={false}
                  className='uc-security-form'
                  onFinish={savePassword}
                >
                  <Form.Item
                    name='currentPassword'
                    label={t('userCenter.current_password')}
                    rules={[
                      { required: true, message: t('userCenter.current_password_required') },
                      {
                        validator: (_, value: string | undefined) => {
                          const password = value ?? '';
                          if (!password) return Promise.resolve();
                          if (password.length <= 72 && utf8ByteLength(password) <= 72) return Promise.resolve();
                          return Promise.reject(new Error(t('userCenter.current_password_rule')));
                        }
                      }
                    ]}
                  >
                    <Input.Password placeholder={t('userCenter.current_password_required')} />
                  </Form.Item>
                  <Form.Item
                    name='newPassword'
                    label={t('userCenter.new_password')}
                    rules={[
                      { required: true, message: t('userCenter.new_password_required') },
                      {
                        validator: (_, value: string | undefined) => {
                          const password = value ?? '';
                          if (!password) return Promise.resolve();
                          const bytes = utf8ByteLength(password);
                          if (
                            password.length >= 8 &&
                            password.length <= 72 &&
                            bytes <= 72 &&
                            !containsPasswordWhitespace(password)
                          ) {
                            return Promise.resolve();
                          }
                          return Promise.reject(new Error(t('userCenter.password_rule')));
                        }
                      }
                    ]}
                  >
                    <Input.Password placeholder={t('userCenter.password_rule')} />
                  </Form.Item>
                  {newPassword ? (
                    <div className='uc-pwd-strength'>
                      <div className='uc-pwd-strength-bar'>
                        <i className={strengthLevel >= 1 ? 'is-on' : ''} />
                        <i className={strengthLevel >= 2 ? 'is-on is-mid' : ''} />
                        <i className={strengthLevel >= 3 ? 'is-on is-strong' : ''} />
                      </div>
                      <span className='uc-pwd-strength-text'>
                        {t('userCenter.strength', { level: strengthLabel })}
                      </span>
                    </div>
                  ) : null}
                  <Form.Item
                    name='confirmPassword'
                    label={t('userCenter.confirm_password')}
                    dependencies={['newPassword']}
                    rules={[
                      { required: true, message: t('userCenter.confirm_password_required') },
                      ({ getFieldValue }) => ({
                        validator(_, value) {
                          if (!value || getFieldValue('newPassword') === value) {
                            return Promise.resolve();
                          }
                          return Promise.reject(new Error(t('userCenter.password_mismatch')));
                        }
                      })
                    ]}
                  >
                    <Input.Password placeholder={t('userCenter.confirm_password_required')} />
                  </Form.Item>
                  <Button type='primary' htmlType='submit' className='uc-primary-btn' loading={passwordSaving}>
                    {t('userCenter.save_changes')}
                  </Button>
                </Form>
              </div>
            </div>

            <div className={tab === 'orders' ? 'uc-tab-panel' : 'uc-tab-panel hidden'}>
              <div className='uc-section'>
                <div className='uc-subtabs'>
                  <button
                    type='button'
                    className={`uc-subtab${orderSubTab === 'package' ? ' is-active' : ''}`}
                    onClick={() => setOrderSubTab('package')}
                  >
                    {t('userCenter.order_packages')}
                  </button>
                  <button
                    type='button'
                    className={`uc-subtab${orderSubTab === 'benefit' ? ' is-active' : ''}`}
                    onClick={() => setOrderSubTab('benefit')}
                  >
                    {t('userCenter.order_benefits')}
                  </button>
                </div>
                <Table<PurchasedOrder>
                  rowKey={(row, index) => row.orderNo?.trim() || `order-${index}`}
                  columns={orderColumns}
                  dataSource={visibleOrders}
                  loading={ordersLoading}
                  pagination={{
                    current: orderPageNum,
                    pageSize: orderPageSize,
                    total: ordersTotal,
                    showSizeChanger: true,
                    onChange: (page, size) => {
                      setOrderPageNum(size === orderPageSize ? page : 1);
                      setOrderPageSize(size);
                    }
                  }}
                  size='middle'
                  className='uc-order-table'
                />
              </div>
            </div>
          </div>
        </section>
      </div>

      <UnbindIdentityModal
        open={unbindTarget != null}
        identityType={unbindTarget?.identityType ?? 'PHONE'}
        identity={unbindTarget?.identity ?? ''}
        onClose={() => setUnbindTarget(null)}
        onSuccess={async () => {
          const next = await refreshAccount();
          if (next) setProfile(mapAccountToProfile(next));
        }}
      />
      <BindIdentityModal
        open={bindTarget != null}
        identityType={bindTarget ?? 'PHONE'}
        onClose={() => setBindTarget(null)}
        onSuccess={async () => {
          const next = await refreshAccount();
          if (next) setProfile(mapAccountToProfile(next));
        }}
      />
      <ChangeIdentityModal
        open={changeTarget != null}
        identityType={changeTarget?.identityType ?? 'PHONE'}
        oldIdentity={changeTarget?.identity ?? ''}
        onClose={() => setChangeTarget(null)}
        onSuccess={async () => {
          const next = await refreshAccount();
          if (next) setProfile(mapAccountToProfile(next));
        }}
      />
    </div>
  );
}
